import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function StairCalc() {
  const [mode, setMode] = useState('basic'); // basic | comprehensive
  const [runMode, setRunMode] = useState('run'); // run | totalRun
  const [unit, setUnit] = useState('in');
  const UN = { in:1, ft:12, m:39.3701, cm:0.393701 };
  const UN_LABEL = { in:'in — inch', ft:'ft — foot', m:'m — meter', cm:'cm — centimeter' };
  const [run, setRun] = useState(120);
  const [rise, setRise] = useState(96);
  const [runPerStep, setRunPerStep] = useState(11);
  const [numSteps, setNumSteps] = useState(12);

  // Convert user inputs to inches
  const riseIn = Number(rise) * UN[unit];
  const runIn = Number(run) * UN[unit];
  const runPerStepIn = Number(runPerStep) * UN[unit];

  const c = useMemo(() => {
    if (mode === 'comprehensive') {
      const n = Math.max(1, Number(numSteps));
      const riserHeight = riseIn / n;
      // In comprehensive, either run-per-step or total-run drives tread depth
      const treadDepth = runMode === 'run' ? runPerStepIn : (n > 1 ? runIn / (n - 1) : 0);
      const totalRun = treadDepth * (n - 1);
      const stringer = Math.sqrt(riseIn * riseIn + totalRun * totalRun);
      const angle = Math.atan(riseIn / (totalRun || 1)) * 180 / Math.PI;
      return { numRisers: n, numTreads: n - 1, riserHeight, treadDepth, totalRun, angle, stringer };
    }
    // Basic
    const numRisers = Math.max(1, Math.round(riseIn / 7.5));
    const riserHeight = riseIn / numRisers;
    const numTreads = numRisers - 1;
    // If user chose "Use One Run", derive Total Run from run-per-step * numTreads
    const effectiveTotalRun = runMode === 'run' ? runPerStepIn * numTreads : runIn;
    const treadDepth = numTreads > 0 ? effectiveTotalRun / numTreads : 0;
    const angle = Math.atan(riseIn / (effectiveTotalRun || 1)) * 180 / Math.PI;
    const stringer = Math.sqrt(riseIn * riseIn + effectiveTotalRun * effectiveTotalRun);
    return { numRisers, numTreads, riserHeight, treadDepth, totalRun: effectiveTotalRun, angle, stringer };
  }, [mode, riseIn, runIn, runPerStepIn, numSteps, runMode]);

  // Variation table: try n-5 .. n+4 risers
  const variations = useMemo(() => {
    const base = c.numRisers;
    const arr = [];
    for (let i = -3; i <= 6; i++) {
      const n = Math.max(2, base + i);
      const rh = riseIn / n;
      const td = (n - 1) > 0 ? (mode === 'comprehensive' ? runPerStepIn : runIn) / (n - 1) : 0;
      const ang = Math.atan(riseIn / ((mode==='comprehensive' ? runPerStepIn*(n-1) : runIn) || 1)) * 180 / Math.PI;
      arr.push({ n, rh, td, ang });
    }
    return arr;
  }, [c.numRisers, riseIn, runIn, runPerStepIn, mode]);

  return (
    <CalcShell look="cnet"
      title="Stair Calculator"
      subtitle="Compute number of steps, riser height, tread depth, stringer length and stair angle — basic or comprehensive."
      about={<>
        <p>Standard residential stair codes typically require riser heights between <b>7"–7.75"</b> and tread depths of at least <b>10"</b>.</p>
        <p>The <b>stringer</b> is the diagonal support; the <b>angle</b> should be 30°–40° for comfortable use.</p>
      </>}
    >
      <div className="flex gap-2 border-b mb-4">
        {[['basic','Basic Version'],['comprehensive','Comprehensive Version']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Unit</label>
              <select value={unit} onChange={e => setUnit(e.target.value)}>{Object.keys(UN).map(u => <option key={u} value={u}>{UN_LABEL[u]}</option>)}</select>
            </div>
          </div>
          <div className="field"><label>Total Rise ({unit})</label><input type="number" step="0.1" value={rise} onChange={e => setRise(e.target.value)}/></div>

          {mode === 'basic' ? (
            <>
              <div className="flex gap-2 mb-2">
                <label className="flex items-center gap-2 text-sm"><input type="radio" checked={runMode==='totalRun'} onChange={() => setRunMode('totalRun')} data-testid="stair-total"/> Use Total Run</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" checked={runMode==='run'} onChange={() => setRunMode('run')} data-testid="stair-one"/> Use One Run (per step)</label>
              </div>
              {runMode === 'totalRun' ? (
                <div className="field"><label>Total Run ({unit})</label><input type="number" step="0.1" value={run} onChange={e => setRun(e.target.value)}/></div>
              ) : (
                <div className="field"><label>One Run — per step ({unit})</label><input type="number" step="0.1" value={runPerStep} onChange={e => setRunPerStep(e.target.value)}/></div>
              )}
            </>
          ) : (
            <>
              <div className="flex gap-2 mb-2">
                <label className="flex items-center gap-2 text-sm"><input type="radio" checked={runMode==='run'} onChange={() => setRunMode('run')}/> Use One Run</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" checked={runMode==='totalRun'} onChange={() => setRunMode('totalRun')}/> Use Total Run</label>
              </div>
              {runMode === 'run' ? (
                <div className="field"><label>Run per Step ({unit})</label><input type="number" step="0.1" value={runPerStep} onChange={e => setRunPerStep(e.target.value)}/></div>
              ) : (
                <div className="field"><label>Total Run ({unit})</label><input type="number" step="0.1" value={run} onChange={e => setRun(e.target.value)}/></div>
              )}
              <div className="field"><label>Number of Steps</label><input type="number" min={2} value={numSteps} onChange={e => setNumSteps(e.target.value)}/></div>
            </>
          )}

          <table className="data-table mt-4"><tbody>
            <tr><td>Number of risers</td><td className="text-right font-semibold">{c.numRisers}</td></tr>
            <tr><td>Number of treads</td><td className="text-right">{c.numTreads}</td></tr>
            <tr><td>Riser height</td><td className="text-right">{c.riserHeight.toFixed(2)} in</td></tr>
            <tr><td>Tread depth</td><td className="text-right">{c.treadDepth.toFixed(2)} in</td></tr>
            <tr><td>Total Run</td><td className="text-right">{c.totalRun.toFixed(2)} in</td></tr>
            <tr><td>Stair angle</td><td className="text-right">{c.angle.toFixed(2)}°</td></tr>
            <tr><td>Stringer length</td><td className="text-right">{c.stringer.toFixed(2)} in</td></tr>
          </tbody></table>
        </div>
        <div className="bg-slate-50 rounded-2xl p-4">
          <h4 className="font-semibold text-slate-700 mb-3 text-center">Stair Diagram (calculated for your inputs)</h4>
          <StairDiagram numRisers={c.numRisers} riserH={c.riserHeight} treadD={c.treadDepth} totalRise={riseIn} totalRun={c.totalRun} />
          <div className="mt-4 text-center">
            <div className="text-xs font-semibold text-slate-600 mb-2">Reference: labelled terminology (total rise, run, stringer, rise, angle, tread, headroom, floor opening)</div>
            <img src="/diagrams/stairs.png" alt="Stair calculator reference diagram" className="mx-auto max-w-xs" data-testid="stair-ref-diagram" />
          </div>
        </div>
      </div>

      {/* Variation table (Basic only) */}
      {mode === 'basic' && (
        <div className="mt-6">
          <h3 className="font-semibold mb-2">10 Stair Variations for Rise = {rise}{unit}</h3>
          <table className="data-table">
            <thead><tr><th>Risers</th><th>Riser Ht (in)</th><th>Tread Depth (in)</th><th>Angle (°)</th></tr></thead>
            <tbody>{variations.map(v => (
              <tr key={v.n}><td>{v.n}</td><td>{v.rh.toFixed(2)}</td><td>{v.td.toFixed(2)}</td><td>{v.ang.toFixed(1)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <StepsPanel steps={[
        `Number of risers = round(Rise / 7.5) = <b>${c.numRisers}</b>`,
        `Riser height = Rise / Risers = <b>${c.riserHeight.toFixed(2)} in</b>`,
        `Tread depth = Run / (Risers − 1) = <b>${c.treadDepth.toFixed(2)} in</b>`,
        `Stringer = √(Rise² + Run²) = <b>${c.stringer.toFixed(2)} in</b>`,
        `Angle = arctan(Rise / Run) = <b>${c.angle.toFixed(2)}°</b>`
      ]} />
    </CalcShell>
  );
}

function StairDiagram({ numRisers, riserH, treadD, totalRise, totalRun }) {
  const svgW = 380, svgH = 280, pad = 30;
  const scale = Math.min((svgW - 2 * pad) / (totalRun || 1), (svgH - 2 * pad) / (totalRise || 1));
  const w = totalRun * scale, h = totalRise * scale;
  const x0 = pad + 20, y0 = svgH - pad;

  // Build stair path
  const path = [`M ${x0} ${y0}`];
  for (let i = 0; i < numRisers; i++) {
    const rh = riserH * scale;
    path.push(`v ${-rh}`); // up (riser)
    if (i < numRisers - 1) path.push(`h ${treadD * scale}`); // horizontal (tread)
  }
  const topX = x0 + treadD * scale * (numRisers - 1);
  const topY = y0 - h;
  path.push(`L ${topX + 20} ${topY}`); // extend at top
  path.push(`L ${topX + 20} ${y0}`); // down
  path.push(`Z`);

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full">
      <defs>
        <linearGradient id="stairGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      {/* Ground */}
      <line x1="10" y1={y0} x2={svgW - 10} y2={y0} stroke="#78716c" strokeWidth="2" />
      {/* Stair shape */}
      <path d={path.join(' ')} fill="url(#stairGrad)" stroke="#78350f" strokeWidth="1.5" />

      {/* Stringer diagonal */}
      <line x1={x0} y1={y0} x2={topX + 20} y2={topY} stroke="#1e40af" strokeWidth="1.5" strokeDasharray="5 3" />

      {/* Total rise */}
      <line x1={svgW - pad + 5} y1={y0} x2={svgW - pad + 5} y2={topY} stroke="#1e40af" strokeWidth="1" />
      <line x1={svgW - pad} y1={y0} x2={svgW - pad + 10} y2={y0} stroke="#1e40af" />
      <line x1={svgW - pad} y1={topY} x2={svgW - pad + 10} y2={topY} stroke="#1e40af" />
      <text x={svgW - pad + 8} y={(y0 + topY) / 2} fontSize="10" fill="#1e40af" fontWeight="700" transform={`rotate(-90 ${svgW - pad + 8} ${(y0 + topY) / 2})`}>Rise {totalRise}"</text>

      {/* Total run */}
      <line x1={x0} y1={y0 + 20} x2={topX} y2={y0 + 20} stroke="#059669" strokeWidth="1" />
      <line x1={x0} y1={y0 + 15} x2={x0} y2={y0 + 25} stroke="#059669" />
      <line x1={topX} y1={y0 + 15} x2={topX} y2={y0 + 25} stroke="#059669" />
      <text x={(x0 + topX) / 2 - 25} y={y0 + 35} fontSize="10" fill="#059669" fontWeight="700">Run {totalRun}"</text>

      {/* Riser height label */}
      <text x={x0 - 24} y={y0 - riserH * scale / 2} fontSize="9" fill="#b91c1c" fontWeight="700">R:{riserH.toFixed(1)}"</text>

      {/* Tread depth label */}
      {numRisers > 1 && (
        <text x={x0 + treadD * scale / 2 - 20} y={y0 - riserH * scale - 4} fontSize="9" fill="#065f46" fontWeight="700">T:{treadD.toFixed(1)}"</text>
      )}

      {/* Angle arc */}
      <path d={`M ${x0 + 25} ${y0} A 25 25 0 0 0 ${x0 + 25 * Math.cos(Math.atan2(h, w))} ${y0 - 25 * Math.sin(Math.atan2(h, w))}`} fill="none" stroke="#7c3aed" strokeWidth="1.5" />
      <text x={x0 + 32} y={y0 - 6} fontSize="10" fill="#7c3aed" fontWeight="700">θ</text>
    </svg>
  );
}
